import { type CSSObject } from '@emotion/react';
import { useTheme } from '../../../theme';

export type UseStylesReturnType = Record<
  'base' | 'destructive' | 'label' | 'wrapper',
  CSSObject
>;

export const useMenuCellStyles = ({
  isMarkVisibleOnlyOnHover,
}: {
  isMarkVisibleOnlyOnHover: boolean;
}): UseStylesReturnType => {
  const theme = useTheme();
  return {
    wrapper: {
      width: '100%',
      display: 'inline-flex',
      alignSelf: 'stretch',
    },
    base: {
      background: 'unset',
      border: 'none',
      textAlign: 'left',
      display: 'inline-flex',
      minHeight: 40,
      minWidth: 204,
      padding: theme.spacing(0, 3),
      alignItems: 'center',
      flexShrink: 0,
      color: theme.menu.item.default,
      userSelect: 'none',
      textDecoration: 'none',
      cursor: 'pointer',
      gap: theme.spacing(3),
      alignSelf: 'stretch',
      width: '100%',

      transition: theme.transitions.create('background', {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeOut,
      }),

      '&:hover': {
        background: theme.menu.background.hover,
        color: theme.menu.item.hover,
        ...(isMarkVisibleOnlyOnHover && {
          '> span:last-child': {
            opacity: 1,
          },
        }),
      },

      '&:focus-visible': {
        outline: `1px solid ${theme.border.interactive.defaultFocused}`,
        background: theme.menu.background.selected,
      },

      '> span:last-child': {
        ...(isMarkVisibleOnlyOnHover && {
          opacity: 0,
        }),
      },

      '&:disabled': {
        cursor: 'not-allowed',
        color: theme.menu.item.disabled,

        '&:hover': {
          background: theme.menu.background.disabledHover,
          color: theme.menu.item.disabled,
        },
      },
    },
    destructive: {
      '&:hover': {
        background: theme.menu.background.destructiveHover,
      },

      '&:focus-visible': {
        outline: `1px solid ${theme.border.interactive.negativeFocused}`,
        background: theme.menu.background.destructiveHover,
      },
    },
    label: {
      flexGrow: 1,
    },
  };
};

export const useMenuCellTitleStyles = (): Record<
  'titleContainer',
  CSSObject
> => {
  const theme = useTheme();
  return {
    titleContainer: {
      minHeight: 24,
      minWidth: 204,
      padding: theme.spacing(1, 2, 0, 2),
      alignItems: 'center',
      display: 'flex',
      flexShrink: 0,
      borderRadius: theme.shape.radius8,
    },
  };
};
